import { useEffect, useMemo, useRef, useState } from 'react';
import MapImage from '@src/assets/images/map.png';
import { LocationData } from '@src/services/dashboard/simLocations/simLocationsApiSliceTypes';
import countriesData from 'world-countries';
import { Popover } from 'antd';

type Country = {
    country: string;
    sims: number;
    lat: number;
    lng: number;
    code: string;
    flagUrl: string;
};

type HoveredCountry = Country & { x: number; y: number };

type WorldMapProps = {
    countries?: LocationData[];
};
export function WorldMap({ countries }: WorldMapProps) {
    const containerRef = useRef<HTMLDivElement>(null);
    const [size, setSize] = useState({ width: 0, height: 0 });
    const [selectedCountry, setSelectedCountry] =
        useState<HoveredCountry | null>(null);

    useEffect(() => {
        if (!containerRef.current) return;

        const resizeObserver = new ResizeObserver((entries) => {
            for (const entry of entries) {
                const { width, height } = entry.contentRect;
                setSize({ width, height });
            }
        });

        resizeObserver.observe(containerRef.current);

        return () => resizeObserver.disconnect();
    }, []);

    const enrichCountries = (locations: LocationData[]): Country[] => {
        return locations
            .map((loc) => {
                const match = countriesData.find(
                    (c) =>
                        c.cca3.toLowerCase() ===
                            loc.countryCode.toLowerCase() ||
                        c.name.common.toLowerCase() ===
                            loc.countryName.toLowerCase()
                );

                if (!match || !match.latlng) return null;

                return {
                    country: loc.countryName,
                    sims: loc.total,
                    lat: match.latlng[0],
                    lng: match.latlng[1],
                    code: loc.countryCode.toLowerCase(),
                    flagUrl: loc.countryFlag,
                };
            })
            .filter(Boolean) as Country[];
    };

    const enriched = useMemo(
        () => enrichCountries(countries || []),
        [countries]
    );

    return (
        <div
            ref={containerRef}
            className="relative w-full h-auto"
            style={{
                userSelect: 'none',
            }}
        >
            <img src={MapImage} alt="Map" className="w-full h-auto" />
            {enriched.map((c, i) => {
                const { x, y } = project(c.lat, c.lng, size.width, size.height);
                return (
                    <Popover
                        key={i}
                        content={
                            <div className="flex flex-col items-center justify-center text-center px-4">
                                <img
                                    src={c.flagUrl}
                                    alt={c.country}
                                    className="w-5 h-4 rounded-sm mb-2"
                                />
                                <div>
                                    <strong>{c.country}</strong>
                                </div>
                                <div>{c.sims.toLocaleString()} SIMs</div>
                            </div>
                        }
                        title={null}
                        trigger="hover"
                        open={selectedCountry?.country === c.country}
                        onOpenChange={(visible) => {
                            if (visible) {
                                setSelectedCountry({ ...c, x, y });
                            } else {
                                setSelectedCountry(null);
                            }
                        }}
                    >
                        <div
                            tabIndex={-1}
                            className="absolute w-6 h-6 flex items-center justify-center cursor-pointer"
                            style={{
                                left: x,
                                top: y,
                                transform: 'translate(-50%, -50%)',
                                userSelect: 'none',
                            }}
                        >
                            {/* Outer animated ring */}
                            <div className="absolute w-full h-full rounded-full bg-blue-300 opacity-30 animate-pulseDot" />

                            {/* Middle ring */}
                            <div className="absolute w-4 h-4 rounded-full bg-blue-400 opacity-50 animate-pulseDot" />

                            {/* Inner dot */}
                            <div className="w-2 h-2 rounded-full bg-primary z-10" />
                        </div>
                    </Popover>
                );
            })}
        </div>
    );
}

function project(
    lat: number,
    lng: number,
    width: number,
    height: number
): { x: number; y: number } {
    const x = ((lng + 180) / 360) * width;

    const adjustedLat = lat - 22;
    const y = ((90 - adjustedLat) / 180) * height;

    return { x, y };
}
